/**
 * What renders when a tool has no bespoke card, or when one failed.
 * Web-only; `index.native.tsx` holds the iOS/Android twin.
 *
 * This used to be a status line: a spinner or a green tick, "Used Profile Get",
 * a "Completed" chip, a truncated approval id, and a collapsible JSON dump. The
 * activity trace now says all of that properly and in the user's language, so
 * repeating it here was duplication in hardcoded English (and hardcoded green
 * and red, which the design system explicitly rules out).
 *
 * What's left is the part the trace genuinely cannot show:
 *
 *   - a failure's reason, as one plain sentence (`tool-failure-model.ts`),
 *     so "Couldn't publish your CV" is followed by the reason rather than
 *     leaving the user to guess, with a Details disclosure, collapsed, that
 *     lists the structured error as readable rows (never raw JSON);
 *   - the server's `display.preview`, which is real content for tools with no
 *     card of their own;
 *   - the raw payload of a successful call, in development only, because a
 *     JSON blob in a conversation is a debugging aid and not a product
 *     surface.
 *
 * When it has none of those it renders nothing at all. A successful tool whose
 * trace line already reads "Read your profile" does not need a grey box under
 * it saying the same thing again.
 */
import React, { useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { MaterialSymbol } from "../../icons/MaterialSymbol";
import { CardShell } from "./card-shell";
import { CARD_PADDING_X, CARD_PADDING_Y } from "./card-ui";
import { ApprovalBanner } from "./approval-banner";
import type { ToolCardProps } from "./types";
import { hasFallbackApproval } from "./fallback-approval";
import { readToolFailure, type ToolFailure } from "./tool-failure-model";

export const FallbackCard: FC<ToolCardProps> = ({
  display,
  stage,
  result,
  isError,
  approvalId,
  toolName,
}) => {
  const { t } = useTranslation("common");
  const [expanded, setExpanded] = useState(false);

  const failed = isError || stage === "failed";
  const failure = failed ? readToolFailure(result) : null;
  const body = failure
    ? (failure.sentence ?? t(`chat.toolResult.${failure.fallbackKey}`))
    : display?.preview;
  const canInspect = !failed && __DEV__ && result != null;

  // Every approval-required tool must remain actionable even before it has a
  // bespoke rich card. Previously an unknown tool rendered nothing here, so
  // its approval could sit forever in "Waiting for you" with no buttons.
  if (hasFallbackApproval(stage, approvalId)) {
    return (
      <CardShell>
        <ApprovalBanner approvalId={approvalId} toolName={toolName} />
      </CardShell>
    );
  }

  if (!body && !canInspect) return null;

  return (
    <CardShell>
      <div style={{ padding: `${CARD_PADDING_Y}px ${CARD_PADDING_X}px` }}>
        {body ? (
          <p
            dir="auto"
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: "18px",
              color: failed ? "#d93025" : "var(--text-secondary)",
            }}
          >
            {body}
          </p>
        ) : null}

        {failure && failure.rows.length > 0 ? (
          <FailureDetails failure={failure} />
        ) : null}

        {canInspect ? (
          <>
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              aria-expanded={expanded}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                marginTop: body ? 8 : 0,
                padding: 0,
                border: 0,
                background: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 12,
                color: "var(--text-tertiary)",
              }}
            >
              <MaterialSymbol
                name={expanded ? "expand_less" : "expand_more"}
                size={14}
              />
              {t("chat.toolResult.rawPayload")}
            </button>
            {expanded ? (
              <pre
                dir="ltr"
                style={{
                  marginTop: 8,
                  marginBottom: 0,
                  padding: 10,
                  borderRadius: "var(--radius-xl)",
                  background: "var(--hover)",
                  color: "var(--text-secondary)",
                  fontSize: 12,
                  lineHeight: 1.5,
                  maxHeight: 200,
                  overflow: "auto",
                }}
              >
                {typeof result === "string"
                  ? result
                  : JSON.stringify(result, null, 2)}
              </pre>
            ) : null}
          </>
        ) : null}
      </div>
    </CardShell>
  );
};

/**
 * The structured error, collapsed by default: code, status and the details
 * the server sent (issues, findings), one row each.
 */
const FailureDetails: FC<{ failure: ToolFailure }> = ({ failure }) => {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  return (
    <div className="tabbio-tool-failure">
      <button
        type="button"
        className="tabbio-tool-failure-toggle"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <MaterialSymbol name={open ? "expand_less" : "expand_more"} size={14} />
        {t("chat.toolResult.details")}
      </button>
      {open ? (
        <dl className="tabbio-tool-failure-rows">
          {failure.rows.map((row, index) => (
            <div key={`${row.label}-${index}`} className="tabbio-tool-failure-row">
              <dt dir="auto">
                {row.labelKey ? t(`chat.toolResult.${row.labelKey}`) : row.label}
              </dt>
              <dd dir="auto">{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  );
};
